import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Radar, ResponsiveContainer, Tooltip
} from "recharts";
import {
  AlertTriangle, CheckCircle, TrendingUp, Target, Zap,
  ChevronLeft, Cpu, ChevronDown, ChevronUp, Sparkles, Printer,
  Clock, User, Calendar, CircleDot, Download, Shield, Activity, Radio, MessageCircle
} from "lucide-react";

import { API } from "../lib/api";

function scoreColor(s) {
  if (s >= 80) return { text: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" };
  if (s >= 60) return { text: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" };
  if (s >= 40) return { text: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200" };
  return { text: "text-red-600", bg: "bg-red-50", border: "border-red-200" };
}

function pillarLabel(k) {
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function pillarColor(score) {
  if (score >= 4) return "#10B981";
  if (score >= 3) return "#F59E0B";
  return "#EF4444";
}

function fmt(v) {
  return typeof v === "number" ? (Number.isInteger(v) ? v : v.toFixed(1)) : v;
}

function severityBadge(sev) {
  const map = { critical: "bg-red-100 text-red-700", high: "bg-orange-100 text-orange-700", medium: "bg-amber-100 text-amber-700" };
  return map[sev] || "bg-gray-100 text-gray-600";
}

const TIER_CONFIG = {
  immediate: { label: "Immediate (0-2 weeks)", color: "border-red-200 bg-red-50", badge: "bg-red-100 text-red-700", icon: Clock },
  short_term: { label: "Short-term (2-6 weeks)", color: "border-amber-200 bg-amber-50", badge: "bg-amber-100 text-amber-700", icon: Calendar },
  structural: { label: "Structural", color: "border-blue-200 bg-blue-50", badge: "bg-blue-100 text-blue-700", icon: Target },
};

const STATUS_OPTIONS = [
  { value: "not_started", label: "Not Started", color: "bg-gray-100 text-gray-600" },
  { value: "in_progress", label: "In Progress", color: "bg-blue-100 text-blue-700" },
  { value: "completed", label: "Completed", color: "bg-emerald-100 text-emerald-700" },
];

function ActionTracker({ projectId }) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetchActions = async () => {
    try {
      const { data } = await axios.get(`${API}/api/projects/${projectId}/actions`, { withCredentials: true });
      setActions(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchActions(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateActions = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/api/projects/${projectId}/actions`, {}, { withCredentials: true });
      fetchActions();
    } catch (e) { console.error(e); }
    finally { setGenerating(false); }
  };

  const updateAction = async (id, field, value) => {
    try {
      await axios.put(`${API}/api/actions/${id}`, { [field]: value }, { withCredentials: true });
      setActions((prev) => prev.map((a) => a.id === id ? { ...a, [field]: value } : a));
    } catch (e) { console.error(e); }
  };

  if (loading) return null;

  if (actions.length === 0) {
    return (
      <div className="text-center py-4">
        <button data-testid="generate-actions-btn" onClick={generateActions} disabled={generating}
          className="bg-slate-900 text-white font-outfit font-semibold px-5 py-2.5 rounded-lg hover:bg-slate-800 transition-colors text-sm disabled:opacity-60">
          {generating ? "Generating..." : "Generate Trackable Actions"}
        </button>
        <p className="font-ibm-plex text-xs text-slate-400 mt-2">Create actionable items you can assign, schedule, and track</p>
      </div>
    );
  }

  // Group by pillar then tier
  const grouped = {};
  actions.forEach((a) => {
    if (!grouped[a.pillar]) grouped[a.pillar] = { immediate: [], short_term: [], structural: [] };
    if (grouped[a.pillar][a.tier]) grouped[a.pillar][a.tier].push(a);
  });

  const completedCount = actions.filter((a) => a.status === "completed").length;
  const totalCount = actions.length;

  return (
    <div>
      {/* Progress bar */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${totalCount ? (completedCount / totalCount) * 100 : 0}%` }} />
        </div>
        <span className="font-ibm-plex text-xs text-slate-500">{completedCount}/{totalCount} done</span>
      </div>

      {Object.entries(grouped).map(([pillar, tiers]) => (
        <div key={pillar} className="mb-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 rounded-full" style={{ background: pillarColor(3) }} />
            <span className="font-outfit text-sm font-bold text-slate-900">{pillarLabel(pillar)}</span>
          </div>
          {["immediate", "short_term", "structural"].map((tier) => {
            const items = tiers[tier] || [];
            if (items.length === 0) return null;
            const cfg = TIER_CONFIG[tier];
            const TierIcon = cfg.icon;
            return (
              <div key={tier} className="mb-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <TierIcon className="w-3 h-3 text-slate-400" />
                  <span className={`text-[10px] font-ibm-plex font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ${cfg.badge}`}>
                    {cfg.label}
                  </span>
                </div>
                <div className="space-y-1.5">
                  {items.map((action) => {
                    const statusCfg = STATUS_OPTIONS.find((s) => s.value === action.status) || STATUS_OPTIONS[0];
                    return (
                      <div key={action.id} data-testid={`action-${action.id}`}
                        className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100 group">
                        <button onClick={() => {
                          const next = action.status === "not_started" ? "in_progress" : action.status === "in_progress" ? "completed" : "not_started";
                          updateAction(action.id, "status", next);
                        }} className="mt-0.5 flex-shrink-0">
                          <CircleDot className={`w-4 h-4 ${action.status === "completed" ? "text-emerald-500" : action.status === "in_progress" ? "text-blue-500" : "text-gray-300"}`} />
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className={`font-ibm-plex text-sm ${action.status === "completed" ? "text-slate-400 line-through" : "text-slate-700"}`}>
                            {action.text}
                          </p>
                          <div className="flex items-center gap-2 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <input type="text" placeholder="Owner..." value={action.owner || ""}
                              onChange={(e) => updateAction(action.id, "owner", e.target.value)}
                              className="text-xs font-ibm-plex border border-gray-200 rounded px-2 py-1 w-28 focus:outline-none focus:ring-1 focus:ring-slate-900" />
                            <input type="date" value={action.deadline || ""}
                              onChange={(e) => updateAction(action.id, "deadline", e.target.value)}
                              className="text-xs font-ibm-plex border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-slate-900" />
                          </div>
                        </div>
                        <span className={`text-[10px] font-ibm-plex font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${statusCfg.color}`}>
                          {statusCfg.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default function ResultsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedAction, setExpandedAction] = useState(null);
  const [showTracker, setShowTracker] = useState(false);
  const [insights, setInsights] = useState(null);
  const [intel, setIntel] = useState(null);
  const [story, setStory] = useState(null);
  const [sprintPulse, setSprintPulse] = useState(null);
  const [stabilization, setStabilization] = useState(null);
  const [signalConfidence, setSignalConfidence] = useState(null);
  const [feedbackThemes, setFeedbackThemes] = useState(null);
  const [realityGap, setRealityGap] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/projects/${id}`, { withCredentials: true })
      .then(({ data }) => setProject(data))
      .catch(() => navigate("/dashboard"))
      .finally(() => setLoading(false));
    axios.get(`${API}/api/projects/${id}/insights`, { withCredentials: true })
      .then(({ data }) => setInsights(data)).catch(() => {});
    axios.get(`${API}/api/intelligence/${id}`, { withCredentials: true })
      .then(({ data }) => setIntel(data)).catch(() => {});
    axios.get(`${API}/api/system-story/${id}`, { withCredentials: true })
      .then(({ data }) => setStory(data)).catch(() => {});
    axios.get(`${API}/api/synq/sprint-pulse/${id}`, { withCredentials: true })
      .then(({ data }) => setSprintPulse(data)).catch(() => {});
    axios.get(`${API}/api/synq/stabilization/${id}`, { withCredentials: true })
      .then(({ data }) => setStabilization(data)).catch(() => {});
    axios.get(`${API}/api/synq/signal-confidence/${id}`, { withCredentials: true })
      .then(({ data }) => setSignalConfidence(data)).catch(() => {});
    axios.get(`${API}/api/synq/feedback-themes/${id}`, { withCredentials: true })
      .then(({ data }) => setFeedbackThemes(data)).catch(() => {});
    axios.get(`${API}/api/synq/reality-gap/${id}`, { withCredentials: true })
      .then(({ data }) => setRealityGap(data)).catch(() => {});
  }, [id, navigate]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!project) return null;

  const sc = scoreColor(project.piq_score);
  const radarData = Object.entries(project.pillars).map(([k, v]) => ({ pillar: pillarLabel(k), score: v, fullMark: 5 }));
  const roleLabel = { individual_contributor: "IC", manager: "Manager", executive: "Executive" }[project.assessment_role] || "";

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Print Header */}
        <div className="hidden print:block mb-6 pb-5 border-b-2 border-slate-900">
          <div className="flex items-start justify-between">
            <div>
              <span className="font-outfit text-xl font-black text-slate-900">SYNQ — Project IQ</span>
              <p className="font-ibm-plex text-xs text-slate-500">Project Intelligence Report</p>
            </div>
            <div className="text-right">
              <p className="font-ibm-plex text-sm font-medium text-slate-700">{new Date().toLocaleDateString("en-US", { dateStyle: "long" })}</p>
              <p className="font-ibm-plex text-xs text-slate-400">Confidential</p>
            </div>
          </div>
        </div>

        {/* Screen Header */}
        <div className="mb-8 animate-slide-up">
          <div className="no-print">
            <button onClick={() => navigate("/dashboard")}
              className="flex items-center gap-1 text-sm font-ibm-plex text-slate-500 hover:text-slate-900 mb-4 transition-colors">
              <ChevronLeft className="w-4 h-4" /> Dashboard
            </button>
          </div>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h1 className="font-outfit text-3xl font-black text-slate-900">{project.name}</h1>
              <p className="font-ibm-plex text-sm text-slate-500 mt-1">
                {project.project_type} · {project.timeline_pressure} timeline · Team of {project.team_size}
                {roleLabel && <> · <span className="font-semibold text-slate-600">{roleLabel} Assessment ({project.question_count || 15}Q)</span></>}
                {" · "}{new Date(project.created_at).toLocaleDateString("en-US", { dateStyle: "medium" })}
              </p>
              {project.mode_label && (
                <div className="mt-2 flex flex-wrap gap-2" data-testid="project-stage-badges">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-900 text-white font-ibm-plex text-[10px] uppercase tracking-widest">
                    {project.mode_label}
                  </span>
                  {project.involves_financial_management && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 font-ibm-plex text-[10px] uppercase tracking-widest">
                      Financial Intelligence
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="no-print flex items-center gap-2">
              <button data-testid="print-report-btn" onClick={() => window.print()}
                className="flex items-center gap-2 border border-gray-300 text-slate-700 font-outfit font-semibold px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm">
                <Printer className="w-4 h-4" /> Print
              </button>
              <button data-testid="whatif-btn" onClick={() => navigate(`/simulate?projectId=${project.id}`)}
                className="flex items-center gap-2 border border-slate-300 text-slate-700 font-outfit font-semibold px-4 py-2 rounded-lg hover:bg-slate-50 transition-colors text-sm">
                <Zap className="w-4 h-4" /> What-If
              </button>
            </div>
          </div>
        </div>

        {/* Score Banner */}
        <div className={`${sc.bg} ${sc.border} border rounded-xl p-6 mb-6 animate-slide-up stagger-1`}>
          <div className="flex items-center justify-between flex-wrap gap-6">
            <div>
              <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-500 mb-1">PIQ Score</p>
              <div className="flex items-end gap-3">
                <span data-testid="piq-score" className={`font-outfit text-6xl font-black ${sc.text}`}>{project.piq_score}</span>
                <span className="font-ibm-plex text-slate-400 text-lg mb-2">/ 100</span>
              </div>
              <span data-testid="risk-level" className={`inline-block font-outfit font-semibold text-lg ${sc.text}`}>{project.risk_level}</span>
            </div>
            <div className="flex gap-6">
              <div className="text-center">
                <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-500 mb-1">Failure Risk</p>
                <p data-testid="failure-probability" className={`font-outfit text-3xl font-black ${sc.text}`}>{project.failure_probability}%</p>
              </div>
              <div className="text-center">
                <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-500 mb-1">Imbalance</p>
                <p data-testid="imbalance-level" className="font-outfit text-3xl font-black text-slate-700">{project.imbalance_level}</p>
              </div>
            </div>
          </div>
        </div>


        {/* Sprint Pulse Banner (Scrum ceremony trigger) */}
        {sprintPulse && project.project_stage === "sprint_mode" && (
          <div data-testid="sprint-pulse-banner"
            className={`border rounded-xl p-5 mb-6 animate-slide-up ${
              sprintPulse.due ? "bg-blue-50 border-blue-200" : "bg-slate-50 border-gray-200"
            }`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  sprintPulse.due ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500"
                }`}>
                  <Activity className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-outfit text-base font-bold text-slate-900">
                    Sprint Pulse — Retrospective Trigger
                  </h3>
                  <p className="font-ibm-plex text-sm text-slate-600 mt-0.5">
                    {sprintPulse.due
                      ? "Pulse is due — capture this sprint's signal now (~5 questions, fatigue-aware)."
                      : `Next pulse: ${sprintPulse.next_pulse_at ? new Date(sprintPulse.next_pulse_at).toLocaleDateString() : "—"} (cadence ${sprintPulse.cadence_days}d)`}
                  </p>
                  <p className="font-ibm-plex text-xs text-slate-400 mt-1">
                    Fatigue level: <span className="font-semibold">{sprintPulse.fatigue_label}</span>
                  </p>
                </div>
              </div>
              {sprintPulse.due && (
                <button data-testid="run-sprint-pulse-btn"
                  onClick={() => navigate(`/project/new`)}
                  className="bg-slate-900 text-white font-outfit font-semibold px-4 py-2 rounded-lg hover:bg-slate-800 text-sm transition-colors flex items-center gap-2">
                  Run Sprint Pulse <Activity className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* SYNQ Adaptive Organizational Intelligence cards */}
        {(stabilization || signalConfidence) && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6" data-testid="aoi-cards">
            {/* Stabilization Summary */}
            {stabilization && (
              <div data-testid="stabilization-card" className="bg-white border border-gray-200 rounded-xl p-5 animate-slide-up">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center">
                    <Shield className="w-4 h-4" />
                  </div>
                  <h3 className="font-outfit text-sm font-bold text-slate-900">Stabilization</h3>
                </div>
                <p className="font-ibm-plex text-sm text-slate-700">{stabilization.summary?.headline}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(stabilization.summary?.critical_pillars || []).map((p) => (
                    <span key={p} className="text-[10px] font-ibm-plex font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-100 text-red-700">{p.replace(/_/g, " ")}</span>
                  ))}
                  {(stabilization.summary?.instability_pillars || []).map((p) => (
                    <span key={p} className="text-[10px] font-ibm-plex font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{p.replace(/_/g, " ")}</span>
                  ))}
                  {(stabilization.summary?.drift_watch_pillars || []).map((p) => (
                    <span key={p} className="text-[10px] font-ibm-plex font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{p.replace(/_/g, " ")}</span>
                  ))}
                  {(stabilization.summary?.stable_pillars || []).slice(0, 2).map((p) => (
                    <span key={p} className="text-[10px] font-ibm-plex font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{p.replace(/_/g, " ")}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Signal Confidence */}
            {signalConfidence && (
              <div data-testid="signal-confidence-card" className="bg-white border border-gray-200 rounded-xl p-5 animate-slide-up">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center">
                    <Activity className="w-4 h-4" />
                  </div>
                  <h3 className="font-outfit text-sm font-bold text-slate-900">Signal Confidence</h3>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <p className="font-ibm-plex text-[10px] uppercase tracking-widest text-slate-400">Strength</p>
                    <p className="font-outfit text-sm font-bold capitalize" data-testid="signal-strength">{signalConfidence.signal_strength}</p>
                  </div>
                  <div>
                    <p className="font-ibm-plex text-[10px] uppercase tracking-widest text-slate-400">Confidence</p>
                    <p className="font-outfit text-sm font-bold capitalize" data-testid="confidence-level">{signalConfidence.confidence_level}</p>
                  </div>
                  <div>
                    <p className="font-ibm-plex text-[10px] uppercase tracking-widest text-slate-400">Drift</p>
                    <p className="font-outfit text-sm font-bold capitalize" data-testid="drift-severity">{signalConfidence.drift_severity}</p>
                  </div>
                </div>
                <p className="font-ibm-plex text-[11px] text-slate-400 mt-3">
                  Coverage {(signalConfidence.factors?.coverage * 100).toFixed(0)}% · {signalConfidence.factors?.response_count} / {signalConfidence.factors?.target_count} responses
                </p>
              </div>
            )}

            {/* Reality Gap Recommendations */}
            {realityGap && realityGap.classification !== "aligned" && (
              <div data-testid="reality-gap-card" className="bg-white border border-gray-200 rounded-xl p-5 animate-slide-up">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center">
                    <AlertTriangle className="w-4 h-4" />
                  </div>
                  <h3 className="font-outfit text-sm font-bold text-slate-900">Reality Gap — {realityGap.label}</h3>
                </div>
                <p className="font-ibm-plex text-xs text-slate-500 mb-2">Abs. gap: {realityGap.absolute_gap}</p>
                <ul className="space-y-1.5">
                  {(realityGap.recommendations || []).slice(0, 3).map((r, i) => (
                    <li key={i} className="font-ibm-plex text-sm text-slate-700 flex gap-2">
                      <span className="text-slate-400">•</span>{r}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Stabilization Recommendations — per pillar */}
        {stabilization && stabilization.pillars?.some((p) => p.recommendations?.length > 0) && (
          <div data-testid="stabilization-detail" className="bg-white border border-gray-200 rounded-xl p-6 mb-6 animate-slide-up">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-4 h-4 text-slate-700" />
              <h3 className="font-outfit text-base font-bold text-slate-900">Stabilization Recommendations</h3>
            </div>
            <p className="font-ibm-plex text-xs text-slate-500 mb-4">
              Systemic, non-accusatory actions per pillar. These guide leadership intervention — never investigate individuals.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {stabilization.pillars
                .filter((p) => p.recommendations?.length > 0)
                .map((p) => (
                  <div key={p.pillar} data-testid={`pillar-rec-${p.pillar}`} className={`border rounded-lg p-4 ${
                    p.severity === "critical" ? "border-red-200 bg-red-50/40"
                    : p.severity === "instability" ? "border-amber-200 bg-amber-50/40"
                    : "border-blue-200 bg-blue-50/40"
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-outfit text-sm font-bold text-slate-900">{p.label}</span>
                      <span className="text-[10px] font-ibm-plex font-semibold uppercase tracking-widest text-slate-500">
                        {p.severity.replace("_", " ")} · {p.score}
                      </span>
                    </div>
                    <ul className="space-y-1">
                      {p.recommendations.map((r, i) => (
                        <li key={i} className="font-ibm-plex text-sm text-slate-700 flex gap-2 leading-snug">
                          <span className="text-slate-400 flex-shrink-0">•</span><span>{r}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Open Feedback Themes (LLM-clustered) */}
        {feedbackThemes && (feedbackThemes.themes?.length > 0 || feedbackThemes.input_count > 0) && (
          <div data-testid="feedback-themes-card" className="bg-white border border-gray-200 rounded-xl p-6 mb-6 animate-slide-up">
            <div className="flex items-center gap-2 mb-4">
              <MessageCircle className="w-4 h-4 text-slate-700" />
              <h3 className="font-outfit text-base font-bold text-slate-900">Open Feedback Themes</h3>
              <span className="text-[10px] font-ibm-plex font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 ml-1">
                {feedbackThemes.input_count} signal{feedbackThemes.input_count !== 1 ? "s" : ""}
              </span>
            </div>
            {feedbackThemes.banner && (
              <p className="font-ibm-plex text-sm text-slate-600 mb-4 italic">"{feedbackThemes.banner}"</p>
            )}
            {feedbackThemes.themes?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {feedbackThemes.themes.map((t, i) => (
                  <div key={i} data-testid={`theme-${i}`} className={`border rounded-lg p-3 ${
                    t.sentiment === "concern" ? "border-amber-200 bg-amber-50/40"
                    : t.sentiment === "positive" ? "border-emerald-200 bg-emerald-50/40"
                    : "border-gray-200 bg-slate-50/40"
                  }`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-outfit text-sm font-semibold text-slate-900">{t.label}</span>
                      <span className="text-[10px] font-ibm-plex font-semibold uppercase tracking-widest text-slate-500">{t.sentiment} · {t.count}</span>
                    </div>
                    {t.sample_quotes?.length > 0 && (
                      <div className="mt-1 space-y-0.5">
                        {t.sample_quotes.map((q, qi) => (
                          <p key={qi} className="font-ibm-plex text-xs text-slate-500 italic">"{q}"</p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="font-ibm-plex text-sm text-slate-500">
                Themes will appear once at least 3 contributors have submitted open feedback.
              </p>
            )}
          </div>
        )}



        {/* Personalized Insights & Market Benchmark */}
        {insights && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 animate-slide-up stagger-1-5">
            <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
              <div>
                <h2 className="font-outfit text-base font-bold text-slate-900">Your Intelligence Summary</h2>
                <p className="font-ibm-plex text-sm text-slate-500 mt-0.5">
                  {insights.user_name && <span className="font-semibold text-slate-700">{insights.user_name}</span>}
                  {insights.team && <> · <span>{insights.team}</span></>}
                  {insights.industry && <> · <span className="text-blue-600">{insights.industry} industry</span></>}
                </p>
              </div>
              <div className="text-right">
                <p className="font-ibm-plex text-xs text-slate-400 uppercase tracking-widest">Role</p>
                <p className="font-outfit text-sm font-bold text-slate-900 capitalize">
                  {insights.assessment_role?.replace(/_/g, " ")}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <div className="bg-slate-50 rounded-lg p-3 border border-gray-100">
                <p className="font-ibm-plex text-[10px] uppercase tracking-widest text-slate-400 mb-1">Industry Average</p>
                <p className="font-outfit text-xl font-black text-slate-700">{insights.benchmark?.avg_piq}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-gray-100">
                <p className="font-ibm-plex text-[10px] uppercase tracking-widest text-slate-400 mb-1">Your Score</p>
                <p className={`font-outfit text-xl font-black ${insights.vs_industry_avg >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                  {insights.piq_score} ({insights.vs_industry_avg >= 0 ? "+" : ""}{insights.vs_industry_avg})
                </p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-gray-100">
                <p className="font-ibm-plex text-[10px] uppercase tracking-widest text-slate-400 mb-1">Percentile</p>
                <p className="font-outfit text-xl font-black text-blue-600">Top {100 - insights.percentile}%</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-gray-100">
                <p className="font-ibm-plex text-[10px] uppercase tracking-widest text-slate-400 mb-1">Common Risk</p>
                <p className="font-outfit text-sm font-bold text-orange-600">{insights.benchmark?.common_risk}</p>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
              <p className="font-ibm-plex text-sm text-blue-800">
                <span className="font-bold">{insights.feel_like_you} out of 100</span> {insights.industry} projects score similarly to yours.
                {insights.vs_industry_avg >= 5 && " You're performing above industry average — maintain this trajectory."}
                {insights.vs_industry_avg < 0 && insights.vs_industry_avg >= -10 && " You're slightly below average — focus on the action items below."}
                {insights.vs_industry_avg < -10 && " Immediate intervention recommended — prioritize the critical action items."}
                {insights.stress_level === "Critical" && " High stress levels detected — burnout risk is elevated."}
                {insights.deadline_pressure === "Critical" && " Critical deadline pressure is compounding project risks."}
              </p>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Radar */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 animate-slide-up stagger-2">
            <h2 className="font-outfit text-base font-bold text-slate-900 mb-4">System Diagnostics</h2>
            <ResponsiveContainer width="100%" height={280}>
              <RadarChart data={radarData} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
                <PolarGrid stroke="#E5E7EB" />
                <PolarAngleAxis dataKey="pillar" tick={{ fontFamily: "IBM Plex Sans", fontSize: 10, fill: "#4B5563" }} />
                <PolarRadiusAxis angle={30} domain={[0, 5]} tickCount={6} tick={{ fontSize: 9, fill: "#9CA3AF" }} />
                <Radar name="Score" dataKey="score" stroke="#0F172A" fill="#0F172A" fillOpacity={0.15} strokeWidth={2} />
                <Tooltip contentStyle={{ fontFamily: "IBM Plex Sans", fontSize: 12, border: "1px solid #E5E7EB" }} formatter={(v) => [`${v}/5`, "Score"]} />
              </RadarChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 gap-2 mt-3">
              {Object.entries(project.pillars).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: pillarColor(v) }} />
                  <span className="font-ibm-plex text-xs text-slate-600 flex-1">{pillarLabel(k)}</span>
                  <span className="font-outfit text-sm font-bold" style={{ color: pillarColor(v) }}>{fmt(v)}/5</span>
                </div>
              ))}
            </div>
          </div>

          {/* Risks */}
          <div className="space-y-4">
            {project.risks?.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl p-6 animate-slide-up stagger-3">
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  <h2 className="font-outfit text-base font-bold text-slate-900">Active Risks</h2>
                </div>
                <div className="space-y-2">
                  {project.risks.map((r, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <span className={`text-xs font-ibm-plex font-semibold px-2 py-0.5 rounded-full ${severityBadge(r.severity)}`}>{r.severity?.toUpperCase()}</span>
                      <span className="font-ibm-plex text-sm text-slate-700">{r.type}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="bg-white border border-gray-200 rounded-xl p-6 animate-slide-up stagger-4">
              <div className="flex items-center gap-2 mb-4">
                <Target className="w-4 h-4 text-slate-700" />
                <h2 className="font-outfit text-base font-bold text-slate-900">Top Risk Areas</h2>
              </div>
              <div className="space-y-3">
                {project.top_risk_areas?.map((k, i) => (
                  <div key={k} className="flex items-center gap-3">
                    <span className="font-outfit text-xl font-black text-slate-200 w-6">#{i + 1}</span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-ibm-plex text-sm font-medium text-slate-700">{pillarLabel(k)}</span>
                        <span className="font-outfit text-sm font-bold" style={{ color: pillarColor(project.pillars[k]) }}>{fmt(project.pillars[k])}/5</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(project.pillars[k] / 5) * 100}%`, background: pillarColor(project.pillars[k]) }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 3-Tier Action Engine */}
        {project.action_items?.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 animate-slide-up stagger-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600" />
                <h2 className="font-outfit text-base font-bold text-slate-900">Action Engine</h2>
              </div>
              <div className="no-print flex border border-gray-200 rounded-lg p-0.5 bg-gray-50">
                <button data-testid="view-plan" onClick={() => setShowTracker(false)}
                  className={`px-3 py-1 text-xs font-outfit font-medium rounded-md transition-all ${!showTracker ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
                  Plan
                </button>
                <button data-testid="view-tracker" onClick={() => setShowTracker(true)}
                  className={`px-3 py-1 text-xs font-outfit font-medium rounded-md transition-all ${showTracker ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
                  Tracker
                </button>
              </div>
            </div>

            {showTracker ? (
              <ActionTracker projectId={id} />
            ) : (
              <div className="space-y-4">
                {project.action_items.map((item, i) => (
                  <div key={i} className="border border-gray-200 rounded-lg overflow-hidden" data-print-expand="true">
                    <button data-testid={`action-item-${i}`}
                      onClick={() => setExpandedAction(expandedAction === i ? null : i)}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center gap-3 text-left">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-outfit font-black text-white flex-shrink-0"
                          style={{ background: pillarColor(item.score) }}>{fmt(item.score)}</div>
                        <div>
                          <p className="font-outfit font-semibold text-slate-900 text-sm">{item.title}</p>
                          <p className="font-ibm-plex text-xs text-slate-400">{pillarLabel(item.pillar)}</p>
                        </div>
                      </div>
                      <span className="no-print">
                        {expandedAction === i ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </span>
                    </button>
                    <div data-print-expand="true" className={`border-t border-gray-100 ${expandedAction === i ? "" : "hidden"}`}>
                      <div className="p-4 space-y-4">
                        <p className="font-ibm-plex text-sm text-slate-500 italic">{item.impact}</p>
                        {[
                          { key: "immediate", label: "Immediate (0-2 weeks)", cfg: TIER_CONFIG.immediate },
                          { key: "short_term", label: "Short-term (2-6 weeks)", cfg: TIER_CONFIG.short_term },
                          { key: "structural", label: "Structural Improvements", cfg: TIER_CONFIG.structural },
                        ].map(({ key, cfg }) => {
                          const actions = item[key];
                          if (!actions?.length) return null;
                          const TierIcon = cfg.icon;
                          return (
                            <div key={key}>
                              <div className="flex items-center gap-1.5 mb-2">
                                <TierIcon className="w-3 h-3 text-slate-400" />
                                <span className={`text-[10px] font-ibm-plex font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ${cfg.badge}`}>
                                  {cfg.label}
                                </span>
                              </div>
                              <ul className="space-y-1.5 ml-1">
                                {actions.map((a, j) => (
                                  <li key={j} className="flex items-start gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                                    <span className="font-ibm-plex text-sm text-slate-700">{a}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}


        {/* System Story Panel */}
        {story && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 animate-slide-up stagger-5-5">
            <div className="flex items-center gap-2 mb-4">
              <Radio className="w-4 h-4 text-purple-600" />
              <h2 className="font-outfit text-base font-bold text-slate-900">System Story</h2>
            </div>
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 border-l-4 border-slate-900">
                <p className="font-ibm-plex text-[10px] uppercase tracking-widest text-slate-400 mb-1">What is happening</p>
                <p className="font-ibm-plex text-sm text-slate-700">{story.what}</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-4 border-l-4 border-amber-500">
                <p className="font-ibm-plex text-[10px] uppercase tracking-widest text-amber-600 mb-1">Why it is happening</p>
                <p className="font-ibm-plex text-sm text-slate-700">{story.why}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
                <p className="font-ibm-plex text-[10px] uppercase tracking-widest text-blue-600 mb-1">What may happen next</p>
                <p className="font-ibm-plex text-sm text-slate-700">{story.next}</p>
              </div>
            </div>
          </div>
        )}

        {/* Intelligence Signals */}
        {intel && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 animate-slide-up stagger-5-7">
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <Shield className="w-3.5 h-3.5 text-blue-600" />
                <p className="font-ibm-plex text-[10px] uppercase tracking-widest text-slate-400">Signal Confidence</p>
              </div>
              <p className={`font-outfit text-lg font-black capitalize ${
                intel.signal_confidence === "high" ? "text-emerald-600" : intel.signal_confidence === "moderate" ? "text-amber-600" : "text-red-600"
              }`}>{intel.signal_confidence}</p>
              <p className="font-ibm-plex text-[10px] text-slate-400">{intel.response_rate}% response rate</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <Clock className="w-3.5 h-3.5 text-blue-600" />
                <p className="font-ibm-plex text-[10px] uppercase tracking-widest text-slate-400">Signal Freshness</p>
              </div>
              <p className={`font-outfit text-lg font-black capitalize ${
                intel.signal_freshness === "fresh" ? "text-emerald-600" : intel.signal_freshness === "aging" ? "text-amber-600" : "text-red-600"
              }`}>{intel.signal_freshness}</p>
              <p className="font-ibm-plex text-[10px] text-slate-400">{intel.days_since_assessment}d ago</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <Activity className="w-3.5 h-3.5 text-purple-600" />
                <p className="font-ibm-plex text-[10px] uppercase tracking-widest text-slate-400">System Drift</p>
              </div>
              <p className={`font-outfit text-lg font-black capitalize ${
                intel.system_drift === "stable" ? "text-emerald-600" : intel.system_drift === "drifting" ? "text-amber-600" : "text-red-600"
              }`}>{intel.system_drift}</p>
              <p className="font-ibm-plex text-[10px] text-slate-400">Variance: {intel.drift_variance}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />
                <p className="font-ibm-plex text-[10px] uppercase tracking-widest text-slate-400">Patterns</p>
              </div>
              <p className="font-outfit text-lg font-black text-slate-700">{intel.patterns?.length || 0}</p>
              <p className="font-ibm-plex text-[10px] text-slate-400">{intel.total_assessments} assessments</p>
            </div>
          </div>
        )}

        {/* Pattern Alerts */}
        {intel?.patterns?.length > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6 space-y-2 animate-slide-up">
            {intel.patterns.map((p, i) => (
              <div key={i} className="flex items-start gap-2">
                <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${p.severity === "critical" ? "text-red-500" : "text-orange-500"}`} />
                <p className="font-ibm-plex text-sm text-slate-700">{p.message}</p>
              </div>
            ))}
          </div>
        )}
        {/* AI Recommendations */}
        <div data-testid="ai-recommendations" className="bg-white border border-gray-200 rounded-xl p-6 mb-6 animate-slide-up stagger-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-blue-600" />
            <h2 className="font-outfit text-base font-bold text-slate-900">AI Strategic Insights</h2>
            <span className="font-ibm-plex text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">Powered by Claude</span>
          </div>
          {project.ai_recommendations ? (
            <div className="font-ibm-plex text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{project.ai_recommendations}</div>
          ) : (
            <div className="flex items-center gap-3 text-slate-400 font-ibm-plex text-sm">
              <Cpu className="w-4 h-4 animate-pulse" /> AI insights are being generated...
            </div>
          )}
        </div>

        {/* Print Footer */}
        <div className="hidden print:block mt-8 pt-4 border-t border-gray-300">
          <div className="flex items-center justify-between">
            <p className="font-ibm-plex text-xs text-slate-500">&copy; {new Date().getFullYear()} Giridhar Alwar. All rights reserved.</p>
            <p className="font-ibm-plex text-xs text-slate-400">SYNQ — Project IQ</p>
          </div>
        </div>

        {/* Bottom Actions */}
        <div className="no-print flex flex-wrap gap-3 animate-slide-up stagger-7">
          <a href={`${API}/api/projects/${project.id}/pdf`} target="_blank" rel="noreferrer"
            data-testid="download-pdf-btn"
            className="flex items-center gap-2 bg-purple-600 text-white font-outfit font-semibold px-5 py-2.5 rounded-lg hover:bg-purple-700 transition-colors">
            <Download className="w-4 h-4" /> Download PDF
          </a>
          <button data-testid="run-whatif-btn" onClick={() => navigate(`/simulate?projectId=${project.id}`)}
            className="flex items-center gap-2 bg-blue-600 text-white font-outfit font-semibold px-5 py-2.5 rounded-lg hover:bg-blue-700 transition-colors">
            <Zap className="w-4 h-4" /> What-If Simulation
          </button>
          <button onClick={() => navigate("/project/new")}
            className="flex items-center gap-2 bg-slate-900 text-white font-outfit font-semibold px-5 py-2.5 rounded-lg hover:bg-slate-800 transition-colors">
            <TrendingUp className="w-4 h-4" /> New Analysis
          </button>
          <button onClick={() => navigate("/history")}
            className="flex items-center gap-2 border border-gray-300 text-slate-700 font-outfit font-semibold px-5 py-2.5 rounded-lg hover:bg-gray-50 transition-colors">
            View History
          </button>
        </div>
      </div>
    </div>
  );
}
